import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, downloadFile, list } from '../lib/api'
import { PageHead, Skel } from '../components/ui'

interface MaskingRule {
  field: string
  label: string
  default_action: string
  action: string
  pattern: string
  enabled: boolean
}

const actionLabel: Record<string, string> = {
  mask: 'ปิดบางส่วน',
  hash: 'แฮช SHA-256',
  drop: 'ลบออก',
}

const actions = [
  { value: 'mask', label: 'ปิดบางส่วน', hint: 'เก็บตัวอักษรแรกและตัวสุดท้าย' },
  { value: 'hash', label: 'แฮช SHA-256', hint: 'แทนด้วย SHA-256 แปดตัวอักษร' },
  { value: 'drop', label: 'ลบออก', hint: 'ลบค่าในคอลัมน์นี้ออกทั้งหมด' },
]

const RULES_KEY = ['masking-rules']
const sampleText = 'ลูกค้า สมชาย ใจดี โทร 081-234-5678 เลขภาษี 1234567890123 อีเมล somchai@example.co.th'

export function Masking() {
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState<string | null>(null)
  const [previewText, setPreviewText] = useState(sampleText)
  const [debounced, setDebounced] = useState(sampleText)

  const rules = useQuery({
    queryKey: RULES_KEY,
    queryFn: () => list<MaskingRule>('/v1/masking/rules'),
    staleTime: 15_000,
  })

  // The preview follows the typing, but waits for a short pause so one request covers a burst
  // of keystrokes instead of one request per character.
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(previewText), 320)
    return () => window.clearTimeout(timer)
  }, [previewText])

  const preview = useQuery({
    queryKey: ['masking-preview', debounced],
    queryFn: () => api<{ output: string }>('/v1/masking/preview', {
      method: 'POST',
      body: JSON.stringify({ text: debounced }),
    }),
    enabled: debounced.trim().length > 0,
    staleTime: 30_000,
  })

  const save = useMutation({
    mutationFn: (v: { field: string; action: string; pattern: string; enabled: boolean }) =>
      apiWrite<MaskingRule>(`/v1/masking/rules/${encodeURIComponent(v.field)}`, 'PUT', {
        action: v.action,
        pattern: v.pattern,
        enabled: v.enabled,
      }),
    onSuccess: (row) => {
      qc.setQueryData<MaskingRule[]>(RULES_KEY, (old) => old
        ? old.map((item) => (item.field === row.field ? row : item))
        : [row])
      setError(null)
      void qc.invalidateQueries({ queryKey: ['masking-preview'] })
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : 'บันทึกนโยบายไม่สำเร็จ')
      void qc.invalidateQueries({ queryKey: RULES_KEY })
    },
    onSettled: () => setSaving(null),
  })

  const rows = rules.data ?? []
  const enabledCount = rows.filter((row) => row.enabled).length
  const busy = save.isPending

  function commit(row: MaskingRule, patch: Partial<MaskingRule>) {
    if (busy) return
    const next = { ...row, ...patch }
    // Write the new value into the cache immediately so a select or checkbox does not jump
    // back to the old value while the request is in flight.
    qc.setQueryData<MaskingRule[]>(RULES_KEY, (old) => old
      ? old.map((item) => (item.field === next.field ? next : item))
      : [next])
    setSaving(row.field)
    save.mutate({ field: next.field, action: next.action, pattern: next.pattern, enabled: next.enabled })
  }

  function commitPattern(row: MaskingRule, value: string) {
    const pattern = value.trim()
    if (pattern === row.pattern) return
    commit(row, { pattern })
  }

  return (
    <>
      <PageHead
        title="นโยบายปิดข้อมูลส่วนบุคคล"
        sub="กำหนดวิธีปิดชื่อคู่ค้า เลขภาษี บัญชี และข้อมูลติดต่อ ก่อนส่งออก CSV · มีผลกับการส่งออกครั้งถัดไป"
        actions={
          <button className="btn btn-ghost"
            onClick={() => void downloadFile('/v1/masking/export.csv', 'auditai-findings-masked.csv')}>
            ดาวน์โหลด CSV ที่ปิดข้อมูล
          </button>
        }
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip">{enabledCount} จาก {rows.length} กฎเปิดใช้</span>
        <span className="chip">ค่าเริ่มต้นตามแคตตาล็อก</span>
        <span className="chip">ทุกการเปลี่ยนแปลงบันทึกในร่องรอยการตรวจสอบ</span>
      </div>

      {rules.isLoading ? (
        <div className="stack"><Skel h={58} /><Skel h={58} /><Skel h={58} /></div>
      ) : rules.isError ? (
        <div className="card card-pad">
          <div className="form-error">โหลดนโยบายไม่สำเร็จ — ลองรีเฟรชหน้าอีกครั้ง</div>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>ฟิลด์</th>
                <th style={{ width: 210 }}>การปิดข้อมูล</th>
                <th style={{ width: 260 }}>รูปแบบตรวจจับในข้อความ</th>
                <th style={{ width: 110 }}>เปิดใช้</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const pending = busy && saving === row.field
                return (
                  <tr key={row.field} style={{ cursor: 'default' }}>
                    <td>
                      <div style={{ color: 'var(--color-ivory)' }}>{row.label || row.field}</div>
                      <div className="mono dim" style={{ fontSize: 11, marginTop: 3 }}>{row.field}</div>
                      <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                        ค่าเริ่มต้น: {actionLabel[row.default_action] ?? row.default_action}
                      </div>
                    </td>
                    <td>
                      <select
                        className="select"
                        aria-label={`การปิดข้อมูล ${row.field}`}
                        value={row.action}
                        disabled={busy}
                        onChange={(e) => commit(row, { action: e.target.value })}
                      >
                        {actions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <div className="dim" style={{ fontSize: 10.5, marginTop: 5 }}>
                        {actions.find((option) => option.value === row.action)?.hint ?? ''}
                      </div>
                    </td>
                    <td>
                      <input
                        key={`${row.field}:${row.pattern}`}
                        className="input mono"
                        defaultValue={row.pattern}
                        disabled={busy}
                        placeholder="ใช้รูปแบบมาตรฐาน"
                        aria-label={`รูปแบบตรวจจับ ${row.field}`}
                        style={{ width: '100%', height: 36, fontSize: 11.5 }}
                        onBlur={(e) => commitPattern(row, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.currentTarget.blur()
                        }}
                      />
                      <div className="dim" style={{ fontSize: 10.5, marginTop: 5 }}>
                        เว้นว่างเพื่อใช้รูปแบบของระบบ
                      </div>
                    </td>
                    <td>
                      <label className="row" style={{ gap: 7, cursor: busy ? 'wait' : 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={row.enabled}
                          disabled={busy}
                          aria-label={`เปิดใช้กฎ ${row.field}`}
                          onChange={(e) => commit(row, { enabled: e.target.checked })}
                          style={{ accentColor: 'var(--color-gold)' }}
                        />
                        <span className="dim" style={{ fontSize: 11.5 }}>
                          {pending ? 'กำลังบันทึก…' : row.enabled ? 'เปิด' : 'ปิด'}
                        </span>
                      </label>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {error ? <div className="form-error" style={{ marginTop: 14 }}>{error}</div> : null}

      <div className="card card-pad" style={{ marginTop: 22 }}>
        <div className="row">
          <div className="card-label">ทดลองปิดข้อมูล</div>
          {preview.isFetching ? (
            <span className="chip" style={{ marginLeft: 'auto' }}>กำลังปิดข้อมูล…</span>
          ) : null}
        </div>
        <p className="dim" style={{ fontSize: 11.5, lineHeight: 1.8, marginTop: 8 }}>
          พิมพ์หรือวางข้อความเพื่อดูผลลัพธ์ตามกฎที่เปิดใช้ · ระบบจะหาอีเมล เบอร์โทร เลขบัตรประชาชน
          และเลขประจำตัวผู้เสียภาษีที่ฝังอยู่ในข้อความ
        </p>
        <div className="field" style={{ marginTop: 12 }}>
          <label htmlFor="masking-preview">ข้อความตัวอย่าง</label>
          <textarea
            id="masking-preview"
            className="input"
            rows={4}
            value={previewText}
            onChange={(e) => setPreviewText(e.target.value)}
            style={{ width: '100%', height: 'auto', minHeight: 96, padding: '10px 12px', resize: 'vertical' }}
          />
        </div>
        <div className="eyebrow" style={{ marginTop: 16 }}>ผลลัพธ์</div>
        {preview.isError ? (
          <div className="form-error" style={{ marginTop: 8 }}>
            {preview.error instanceof Error ? preview.error.message : 'ทดลองปิดข้อมูลไม่สำเร็จ'}
          </div>
        ) : (
          <pre className="mono" style={{
            margin: '8px 0 0',
            padding: '12px 14px',
            minHeight: 64,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: 12.5,
            lineHeight: 1.8,
            background: 'var(--color-panel, rgba(255,255,255,0.03))',
            border: '1px solid var(--color-hairline, rgba(255,255,255,0.08))',
            borderRadius: 8,
          }}>
            {preview.data?.output ?? (previewText.trim() ? 'กำลังปิดข้อมูล…' : '')}
          </pre>
        )}
        <div className="dim" style={{ fontSize: 10.5, lineHeight: 1.8, marginTop: 10 }}>
          ตัวอย่างนี้ใช้สำหรับทดสอบเท่านั้น ไม่ถูกบันทึก และไม่ถูกส่งออก
        </div>
      </div>

      <div className="card card-pad" style={{ marginTop: 22 }}>
        <div className="card-label">การทำงานของนโยบาย</div>
        <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
          <li>ปิดบางส่วนเก็บตัวอักษรแรกและตัวสุดท้ายไว้ เพื่อให้ผู้ตรวจสอบยังรู้จักรายการได้</li>
          <li>แฮชใช้ SHA-256 แปดตัวอักษรแรก ค่าเดิมให้ผลเดิม จึงใช้จับคู่ได้โดยไม่เปิดเผยค่า</li>
          <li>ลบออกจะทำให้คอลัมน์ว่างในไฟล์ส่งออก แต่แถวและร่องรอยการตรวจสอบยังอยู่ครบ</li>
          <li>รูปแบบตรวจจับใช้กับข้อความอิสระ เช่น สรุปข้อสังเกต ที่ไม่มีชื่อคอลัมน์ตายตัว</li>
        </ul>
      </div>
    </>
  )
}
